import { useEffect, useState } from "react";
import { Monitor, Tablet, Smartphone, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import ContentSections from "../public/ContentSections";
import RelatedContent from "../public/RelatedContent";
import { fieldsFor } from "../../config/contentSeo";
import api from "../../lib/api";

const stripText = (h) => (h || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

function buildContent(seo, entityType) {
  const c = seo?.content || {};
  const fields = fieldsFor(entityType)
    .map((f) => ({ key: f.key, label: f.label, html: c[f.key] || "" }))
    .filter((f) => f.html && stripText(f.html));
  const faqs = (seo?.faqs || []).filter((f) => f.enabled !== false && f.question)
    .map((f) => ({ question: f.question, answer: f.answer || "" }));
  const external_refs = (seo?.external_refs || []).filter((r) => r.enabled !== false && r.url)
    .map((r) => ({ title: r.title, url: r.url, description: r.description, new_tab: r.new_tab, rel: "noopener" }));
  return { fields, faqs, external_refs };
}

const DEVICES = {
  desktop: { icon: Monitor, w: "100%", label: "Desktop" },
  tablet: { icon: Tablet, w: "768px", label: "Tablet" },
  mobile: { icon: Smartphone, w: "390px", label: "Mobile" },
};

export default function LivePreview({ open, onOpenChange, entityType, entity, seo, title = "Draft Preview" }) {
  const [device, setDevice] = useState("desktop");
  const [related, setRelated] = useState(null);
  const [loadingRel, setLoadingRel] = useState(false);
  const content = buildContent(seo || {}, entityType);
  const slug = entity?.slug;

  useEffect(() => {
    if (!open || !slug) { setRelated(null); return; }
    setLoadingRel(true);
    api.get("/related-content", { params: { entity_type: entityType, slug } })
      .then(({ data }) => setRelated(data)).catch(() => setRelated(null)).finally(() => setLoadingRel(false));
  }, [open, slug, entityType]);

  const empty = content.fields.length === 0 && content.faqs.length === 0 && content.external_refs.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="live-preview-dialog">
        <DialogHeader className="p-4 pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>Rendered with the live storefront theme &amp; components. This preview is admin-only — never indexed, never public.</DialogDescription>
            </div>
            <div className="flex gap-1 rounded-lg border border-white/10 p-1">
              {Object.entries(DEVICES).map(([k, d]) => {
                const Icon = d.icon;
                return (
                  <Button key={k} type="button" variant={device === k ? "default" : "ghost"} size="sm"
                    className={`gap-1 h-8 ${device === k ? "cf-gold-btn" : "text-white/60"}`} onClick={() => setDevice(k)} data-testid={`preview-device-${k}`}>
                    <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{d.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto bg-black/40 p-4">
          <div className="mx-auto bg-[#0b0b0f] rounded-xl border border-white/10 transition-all duration-300 overflow-hidden"
               style={{ width: DEVICES[device].w, maxWidth: "100%" }} data-testid="preview-frame">
            <div className="p-6 sm:p-10">
              <h1 className="font-playfair text-3xl text-white mb-2">{entity?.name || entity?.title || "Preview"}</h1>
              {empty ? <p className="text-white/40 text-sm py-8">No content to preview yet — add some content sections or FAQs.</p>
                : <ContentSections content={content} flat />}
              {loadingRel ? <div className="mt-8 text-white/40 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading related content…</div>
                : <RelatedContent data={related} />}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
